import { Box, Stack, ToggleButton, Typography, useTheme } from "@mui/material"
import { ElementType, ReactElement } from "react"

interface TileButtonProps {
    title: string
    value: string
    selected: boolean
    onClick: (value: string) => void
    Icon: ElementType
}
/**
 * A tile button component. Used to display a tile button with an icon and title.
 */
const TileButton = ({
    title,
    value,
    selected,
    onClick,
    Icon,
}: TileButtonProps): ReactElement => {
    const theme = useTheme()
    return (
        <ToggleButton
            value={value}
            sx={{
                boxSizing: "border-box",
                boxShadow: 1,
                border: `1px solid ${theme.palette.grey[400]}`,
                borderRadius: { xs: 2, md: 4 },
                padding: { xs: 1.2, md: 5 },
                textTransform: "none",
                width: "100%",
                height: "100%",
                alignItems: "stretch",
                justifyContent: "flex-start",
                overflow: "hidden",
                "&.MuiToggleButtonGroup-grouped": {
                    border: `1px solid ${theme.palette.grey[400]}`,
                    borderRadius: { xs: 2, md: 4 },
                    margin: 0,
                },
                "&.Mui-selected, &.Mui-selected:hover": {
                    borderColor: theme.palette.primary.main,
                    boxShadow: 3,
                    backgroundColor: theme.palette.primary.light,
                },
                "&:hover": {
                    borderColor: theme.palette.primary.main,
                    boxShadow: 3,
                    borderWidth: 1,
                },
            }}
            className="tile-button"
            color="primary"
            onClick={() => onClick(value)}
            selected={selected}
        >
            <Stack
                spacing={{ xs: 0, md: 2 }}
                direction="column"
                justifyContent="flex-start"
                alignItems="center"
                flexDirection={{ xs: "row", md: "column" }}
                width="100%"
            >
                <Box
                    paddingRight={{ xs: 4, sm: 8, md: 0 }}
                    paddingLeft={{ xs: 2, md: 0 }}
                    display="flex"
                    alignContent="center"
                >
                    <Icon
                        sx={{
                            fontSize: { xs: "40px", sm: "60px", lg: "85px" },
                            color: selected
                                ? "primary"
                                : theme.palette.text.primary,
                        }}
                    />
                </Box>
                <Typography
                    variant="h6"
                    color={selected ? "primary" : "text.primary"}
                >
                    {title}
                </Typography>
            </Stack>
        </ToggleButton>
    )
}

export default TileButton
